import { Link, useLocation } from 'react-router-dom';
import type { RecordOpen } from '../../tasks/model.ts';
import { useMemo, useState, type ReactNode } from 'react';
import { OpenedRecord } from '../../components/OpenedRecord.tsx';
import { useSeen } from '../../tasks/seen.ts';
import { TaskRow } from '../../components/tasks/TaskRow.tsx';
import { ClosedTaskRow } from '../../components/tasks/ClosedTaskRow.tsx';
import { TaskCard } from '../../components/tasks/TaskCard.tsx';
import { NewTaskSheet } from '../../components/tasks/NewTask.tsx';
import { chip, plain } from '../../components/tasks/Fields.tsx';
import { agencyToday } from '../../components/tasks/FollowUp.tsx';
import { useClientDocuments } from '../../api/documents.ts';
import { ClientFoldersPanel } from '../../components/ClientFolders.tsx';
import { DocumentsBoundary } from '../../components/DocumentsBoundary.ts';
import { useClientFolders } from '../../api/uploads.ts';
import { mergeDocuments } from '../../documentsMerge.ts';
import type { ClientBanner } from '../../api/types.ts';
import { useClientTimeline } from '../../api/queries.ts';
import { useWhereaboutsReports } from '../../api/whereaboutsReports.ts';
import {
  useClientClosedTasks, useClientCompensation, useClientReports, useClientTasks, usePropertyTimeline,
} from '../../api/navigation.ts';
import { CurrentStanding } from '../../components/CurrentStanding.tsx';
import { CourtOrderPanel } from '../../components/CourtOrderPanel.tsx';
import { OrderRecords } from '../../components/OrderRecords.tsx';
import { BondsPanel } from '../../components/BondsPanel.tsx';
import { MarshallingPanel } from '../../components/MarshallingPanel.tsx';
import { AccountsPanel, EntriesPanel, ReconciliationPanel } from '../../components/LedgerPanels.tsx';
import { LedgerImport } from '../../components/LedgerImport.tsx';
import { useClientLedger } from '../../api/ledger.ts';
import { FurtherMarshallingPanel } from '../../components/FurtherMarshallingPanel.tsx';
import { CommissionLineView } from '../../components/CommissionLines.tsx';
import { useClientCommission } from '../../api/commissions.ts';
import { AdvancedPanel } from '../../components/AdvancedPanel.tsx';
import { WhereaboutsPanel } from '../../components/WhereaboutsPanel.tsx';
import { VisitStandardPanel } from '../../components/VisitStandardPanel.tsx';
import { CaseTeamPanel } from '../../components/CaseTeamPanel.tsx';
import { PropertiesPanel } from '../../components/PropertiesPanel.tsx';
import { PlacementTimeline } from '../../components/PlacementTimeline.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';
import { formatMoney } from '../../components/DashboardFrame.tsx';
import { formatDate } from '../../dates.ts';
import { SectionPanel } from '../ClientRecord.tsx';
import { BAR_ACTION } from '../../components/barAction.ts';
import { DepartmentSelect } from '../../components/tasks/DepartmentSelect.tsx';
import { inDepartment, type DepartmentChoice } from '../../tasks/departmentFilter.ts';
import { CommunicationsFeed } from '../../components/communications/CommunicationsFeed.tsx';
import { ReportFiling } from '../../components/ReportFiling.tsx';
import { Cell, Panel } from '../../components/Panel.tsx';
import { CourtReportDraft } from './CourtReportDraft.tsx';

/**
 * The client sections other than the Dashboard (docs/navigation-brief.md,
 * section 5). Each holds the panels the old single page had, moved whole, and
 * the new reads. Each read is refused by the API unless the reader holds the
 * section; nothing here decides that.
 */

/** A stored code, PRO_BONO, as words: Pro bono. */
function words(code: string) {
  const w = code.replace(/_/g, ' ').toLowerCase();
  return w.charAt(0).toUpperCase() + w.slice(1);
}

function isClosed(status: string | null) {
  return status === 'Deceased' || status === 'Deceased, discharged' || status === 'Discharged';
}

/**
 * Space for what is coming: a panel like every other, its bar saying "Coming",
 * so a reserved section reads as planned rather than broken or empty.
 */
export function Reserved({ title, children, span = 12 }: { title: string; children: ReactNode; span?: 12 | 8 | 6 | 4 }) {
  return (
    <Panel title={title} note="Coming" span={span}>
      <div className="max-w-[48rem] px-4 py-3 text-[0.9375rem] text-neutral-700">{children}</div>
    </Panel>
  );
}

function Failure({ what, query }: { what: string; query: { error: Error | null; refetch: () => unknown; isFetching: boolean } }) {
  return (
    <LoadFailure what={what} error={query.error!} onRetry={() => void query.refetch()}
                 retrying={query.isFetching} forbiddenIsSession={false} />
  );
}

// ---------------------------------------------------------------------------- Legal

export function LegalSection({ clientId, legalStatus, hasSw }: { clientId: string; legalStatus: string | null; hasSw: boolean }) {
  // Where they live comes from the SW timeline; read only by a reader who holds SW.
  const history = useClientTimeline(clientId, hasSw);
  return (
    <div className="grid12">
      <Cell span={6}>
        <CurrentStanding clientId={clientId} legalStatus={legalStatus}
                         entries={hasSw ? history.data?.entries : []} placementStart={history.data?.placement_start} />
      </Cell>
      <CommissionPanel clientId={clientId} />
      <Panel title="Court orders" span={12}>
        <div className="pt-3"><CourtOrderPanel clientId={clientId} /></div>
      </Panel>
      {/* 162 (P-35): each order's bond, recorded against the order; the bond completes "Procure the bond" */}
      <Panel title="Bonds" span={12}>
        <div className="pt-3"><BondsPanel clientId={clientId} /></div>
      </Panel>
      {/* 166 (P-33, A-#354): whether an order authorizes marshalling more than a partial did; Legal's to record */}
      <Panel title="Further marshalling" span={12}>
        <div className="pt-3"><FurtherMarshallingPanel clientId={clientId} /></div>
      </Panel>
      {/* Last, and collapsed: ending a case is irreversible and starts statutory clocks. */}
      <Cell span={12}><AdvancedPanel clientId={clientId} /></Cell>
    </div>
  );
}

function CommissionPanel({ clientId }: { clientId: string }) {
  // 167 (P-24): one line per acting order, its steps read from the records; nobody sets a state
  const q = useClientCommission(clientId);
  const [said, setSaid] = useState<string | null>(null);
  const lines = q.data?.lines ?? [];
  const racing = lines.filter((l) => l.needs_commission);
  return (
    <SectionPanel title="Commission" span={6}
                  aside={<Link to="/legal" className="link inline-flex min-h-[44px] items-center text-sm font-semibold">Commission tracker</Link>}>
      {q.error && !q.data ? (
        <Failure what="The commission" query={q} />
      ) : !q.data ? (
        <p className="px-4 pb-3 text-neutral-600">Loading…</p>
      ) : lines.length === 0 ? (
        <p className="px-4 pb-3 text-neutral-600">No live appointment on file to commission.</p>
      ) : racing.length === 0 ? (
        <p className="px-4 pb-3 text-neutral-700">No commission is needed for this appointment.</p>
      ) : (
        <div className="px-4 pb-3">
          {said && <p role="status" className="pb-2 text-sm text-neutral-800">{said}</p>}
          <ul className="divide-y divide-neutral-100">
            {racing.map((l) => (
              <li key={l.court_order_id ?? 'none'} className="py-2">
                <CommissionLineView line={l} mayRecord={q.data!.may_record} mayResolve={q.data!.may_resolve} onSaid={setSaid} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* 154: each order's commission and oath and designation, recorded against the order */}
      {racing.length > 0 && <OrderRecords clientId={clientId} />}
    </SectionPanel>
  );
}

// ---------------------------------------------------------------------------- Finance

export function FinancialsSection({ clientId }: { clientId: string }) {
  const q = useClientCompensation(clientId);
  return (
    <div className="grid12">
      <SectionPanel title="Compensation (read-only)" span={6}>
        {q.error && !q.data ? (
          <Failure what="Compensation" query={q} />
        ) : !q.data ? (
          <p className="px-4 pb-3 text-neutral-600">Loading…</p>
        ) : q.data.compensation.length === 0 ? (
          <p className="px-4 pb-3 text-neutral-600">No compensation is recorded for this client.</p>
        ) : (
          <div className="overflow-x-auto px-4 pb-3">
            <table className="w-full text-left text-[0.9375rem]">
              <thead className="text-sm text-neutral-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Kind</th>
                  <th className="py-2 pr-4 font-medium">Monthly</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">From</th>
                  <th className="py-2 pr-4 font-medium">Last billed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {q.data.compensation.map((c) => (
                  <tr key={c.id} className={c.is_active ? '' : 'text-neutral-500'}>
                    <td className="py-2 pr-4">{c.kind ? words(c.kind) : '—'}{!c.is_active && ' (ended)'}</td>
                    <td className="py-2 pr-4 tabular-nums">{c.monthly_amount ? formatMoney(c.monthly_amount) : '—'}</td>
                    <td className="py-2 pr-4">{c.status ?? '—'}</td>
                    <td className="py-2 pr-4">{c.starts_on ? formatDate(c.starts_on) : '—'}</td>
                    <td className="py-2 pr-4">{c.last_billed_date ? formatDate(c.last_billed_date) : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-sm text-neutral-500">Marking compensation transferred comes next, for AP/AR.</p>
          </div>
        )}
      </SectionPanel>
      <Panel title="Benefits" note="Coming" span={6}>
        <p className="px-4 py-3 text-[0.9375rem] text-neutral-700">Social Security and other entitlements, established and received.</p>
      </Panel>
      {/* 166 (P-33): each bank's marshalling as dated steps; "Marshal client accounts" completes from them */}
      <Panel title="Accounts and marshalling" span={12}>
        <div className="pt-3"><MarshallingPanel clientId={clientId} /></div>
      </Panel>
      {/* money phase 4 (screens R4, a198aa36): the accounts' money, "...1234" everywhere, on slices 1-3's routes */}
      <Panel title="Accounts" span={12}>
        <div className="pt-3"><AccountsPanel clientId={clientId} /></div>
      </Panel>
      <Panel title="Entries" span={12}>
        <div className="pt-3"><LedgerEntries clientId={clientId} /></div>
      </Panel>
      <Panel title="Reconciliation" span={12}>
        <div className="pt-3"><ReconciliationPanel clientId={clientId} /></div>
      </Panel>
    </div>
  );
}

/** The entries, and the import from Quicken for whoever the server says may import (AP/AR and tier 1). */
function LedgerEntries({ clientId }: { clientId: string }) {
  const ledger = useClientLedger(clientId);
  const [importing, setImporting] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const mayImport = ledger.data?.may.import === true;
  return (
    <>
      {mayImport && !importing && (
        <div className="px-4"><button type="button" className="min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
          onClick={() => setImporting(true)}>Import from Quicken</button></div>
      )}
      {said && <p role="status" className="px-4 pt-2 text-sm text-neutral-800">{said}</p>}
      {importing && ledger.data && (
        <div className="px-4"><LedgerImport clientId={clientId} accounts={ledger.data.accounts} categories={ledger.data.categories}
          onDone={(m) => { setImporting(false); if (m) setSaid(m); }} /></div>
      )}
      <EntriesPanel clientId={clientId} />
    </>
  );
}

// ---------------------------------------------------------------------------- Property

export function PropertySection({ clientId }: { clientId: string }) {
  const q = usePropertyTimeline(clientId);
  return (
    <div className="grid12">
      <Cell span={12}><PropertiesPanel clientId={clientId} /></Cell>
      <SectionPanel title="Property timeline">
        {q.error && !q.data ? (
          <Failure what="The property timeline" query={q} />
        ) : !q.data ? (
          <p className="px-4 pb-3 text-neutral-600">Loading…</p>
        ) : q.data.events.length === 0 ? (
          <p className="px-4 pb-3 text-neutral-600">
            No dispositions recorded yet. Sales, surrenders, evictions and the rest appear here as they are recorded,
            with site visits and tenure to follow.
          </p>
        ) : (
          <ol className="divide-y divide-neutral-100 px-4 pb-2">
            {q.data.events.map((e) => (
              <li key={e.id} className={`flex min-h-[44px] flex-wrap items-baseline gap-x-3 py-2 ${e.withdrawn || e.withdraws_event_id ? 'text-neutral-500' : ''}`}>
                <span className="w-28 shrink-0 text-sm tabular-nums">
                  {e.effective_on ? formatDate(e.effective_on) : 'Date not known'}
                </span>
                <span className="font-semibold">
                  {e.withdraws_event_id ? `Withdrawn: ${e.disposition}` : e.disposition}
                  {e.withdrawn && ' (withdrawn)'}
                </span>
                <span className="min-w-0 flex-1 text-sm text-neutral-600">{e.property_name}</span>
                {e.source && <span className="text-xs text-neutral-500">{e.source}</span>}
              </li>
            ))}
          </ol>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------- Documents, Communications

// ---------------------------------------------------------------------------- Documents

/**
 * The client's Clio copies (132), as Clio's own folders show them (Arthur, 20 October, for Judah): the explorer
 * (components/DocumentsExplorer.tsx) over the one audited list. Opening is through Procura and audited; only a
 * document that has passed its malware scan opens.
 */
export function DocumentsSection({ clientId }: { clientId: string }) {
  const q = useClientDocuments(clientId);
  const own = useClientFolders(clientId);
  // One merge over the client's two lists (documentsMerge.ts): Clio's Social Services, Finance, Legal, Vital Docs and
  // Real Estate shown inside the client's folders; every other Clio folder in a read-only group at the top level.
  const merged = useMemo(() => (q.data && own.data
    ? mergeDocuments(own.data.folders, own.data.uploads, q.data.documents) : null), [q.data, own.data]);
  if ((q.error && !q.data) || (own.error && !own.data)) {
    return <Failure what="Documents" query={q.error && !q.data ? q : own} />;
  }
  if (!merged || !own.data) return <SectionPanel title="Client folders"><p className="px-4 pb-3 text-neutral-600">Loading…</p></SectionPanel>;
  return (
    <>
    {/* Every Clio folder off the list is in the tree's read-only "Folders taken directly from Clio" (Arthur, 14:31). */}
    {/* A crash inside shows a message and Back to Documents, never a blank page (21 October, 16:50). */}
    <DocumentsBoundary>
      <ClientFoldersPanel clientId={clientId} data={own.data} merged={merged} />
    </DocumentsBoundary>
    </>
  );
}

export function CommunicationsSection({ clientId }: { clientId: string }) {
  return (
    <SectionPanel title="Communications"
      aside={<Link to="/communications/unmatched" className="text-sm font-semibold text-brand-text underline">Not yet matched</Link>}>
      <CommunicationsFeed clientId={clientId} />
    </SectionPanel>
  );
}

// ---------------------------------------------------------------------------- Reports

export function ReportsSection({ clientId }: { clientId: string }) {
  const q = useClientReports(clientId);
  // From a filing task (A-#191): that deadline's filing form, open from the start.
  const asked = useLocation().state as RecordOpen | null;
  const fileFor = asked?.file_deadline ?? null;
  // A-#194: from a task its filing closed, or the Timeline: that filing's row, scrolled to and marked
  const showFiling = asked?.show_filing ?? null;
  const outstanding = q.data?.reports.filter((r) => r.state === 'Not started' || r.state === 'In preparation') ?? [];
  const rest = q.data?.reports.filter((r) => !(r.state === 'Not started' || r.state === 'In preparation')) ?? [];
  const mayFile = q.data?.may_file === true;
  return (
    <SectionPanel title="Reports owed to the court">
      {q.error && !q.data ? (
        <Failure what="Reports" query={q} />
      ) : !q.data ? (
        <p className="px-4 pb-3 text-neutral-600">Loading…</p>
      ) : q.data.reports.length === 0 ? (
        <p className="px-4 pb-3 text-neutral-600">No reports are owed for this client.</p>
      ) : (
        <div className="px-4 pb-3">
          <ReportList clientId={clientId} mayFile={mayFile} heading="Outstanding" rows={outstanding} empty="Nothing outstanding." fileFor={fileFor} />
          <ReportList clientId={clientId} mayFile={mayFile} heading="Filed and closed" rows={rest} empty="None yet." showFiling={showFiling}
                      openedFrom={asked?.opened_from ?? null} />
          {!mayFile && (
            <p className="mt-2 text-sm text-neutral-600">
              The Director of Compliance, the compliance paralegal and tiers 1–2 mark reports filed.
            </p>
          )}
        </div>
      )}
      <div className="px-4 pb-3"><CourtReportDraft clientId={clientId} /></div>
    </SectionPanel>
  );
}

function ReportList({ clientId, mayFile, heading, rows, empty, fileFor = null, showFiling = null, openedFrom = null }: {
  clientId: string; mayFile: boolean; heading: string; rows: import('../../api/navigation.ts').ClientReport[]; empty: string;
  fileFor?: string | null; showFiling?: string | null; openedFrom?: RecordOpen['opened_from'] | null;
}) {
  return (
    <div className="mt-1">
      <h3 className="text-sm font-semibold text-neutral-800">{heading}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-neutral-600">{empty}</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r) => (
            <OpenedRecord key={r.deadline_id} id={`filing-${r.deadline_id}`} opened={r.deadline_id === showFiling} from={openedFrom}
                          className="flex min-h-[44px] flex-wrap items-center gap-x-3 py-1.5">
              <span className="min-w-0 flex-1 text-neutral-900">{r.label}{r.period_year ? ` · ${r.period_year}` : ''}</span>
              <span className="text-xs text-neutral-600">{r.state}{r.is_provisional ? ' · estimate' : ''}</span>
              <span className={`text-sm tabular-nums ${r.urgency === 1 ? 'font-semibold text-critical-edge' : 'text-neutral-700'}`}>
                {r.filed_date ? `filed ${formatDate(r.filed_date)}` : r.filed_date_unknown ? 'filed, date not known'
                  : r.due_date ? `due ${formatDate(r.due_date)}` : 'no date'}
              </span>
              {mayFile && <ReportFiling clientId={clientId} row={r} startOpen={r.deadline_id === fileFor} />}
            </OpenedRecord>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------- Tasks

/**
 * The client's tasks (147): each row the Task object (contract v3, route "the client's own tasks"), opening the task
 * card; the reader's own rows reported seen (ruling 17); "New task" for this client (actions are recorded from its department sections, A-#144).
 */
export function TasksSection({ clientId, client }: { clientId: string; client: { id: string; name: string; ref: string | null } }) {
  const q = useClientTasks(clientId);
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'new' | null>(null);
  // A-#211 fix 2: Open (today's list, unchanged) or Closed; local state, never the address. The closed list is
  // fetched the first time its tab is opened, and kept.
  const [tab, setTab] = useState<'open' | 'closed'>('open');
  const [closedAsked, setClosedAsked] = useState(false);
  // P-23: ONE department choice for both tabs (P-23 D, Arthur: shared), kept as the tabs switch. The Open tab filters
  // on the page (it holds the whole open list); the Closed tab asks the server, which filters before paging. Seen is a
  // record of an open holding (ruling 17) and follows only the rows on screen (A-#257 1A): the Open tab's rows as
  // filtered, never the closed list, and nothing while the Closed tab is the one shown.
  const [department, setDepartment] = useState<DepartmentChoice>('');
  const closed = useClientClosedTasks(clientId, closedAsked, department);
  const all = q.data?.tasks ?? [];
  const tasks = inDepartment(all, department);
  useSeen(q.data && tab === 'open' ? tasks : undefined);
  const today = agencyToday();
  const ownOnly = tab === 'open' ? q.data?.own_only : closed.data?.pages[0]?.own_only;
  const closedTasks = closed.data?.pages.flatMap((p) => p.tasks) ?? [];
  const choose = (t: 'open' | 'closed') => { setTab(t); if (t === 'closed') setClosedAsked(true); };
  // deploy 8 (A-#225, A-#234): the title bar reads "Tasks", New task in it on the right, drawn as the Documents
  // bar's "+" is; whose tasks these are is a line under the tabs
  return (
    <SectionPanel title="Tasks" action={<button type="button" className={BAR_ACTION} onClick={() => setSheet('new')}>New task</button>}>
      <div className="px-4 pb-2">
        <div role="tablist" aria-label="Tasks" className="flex flex-wrap gap-2">
          <button type="button" role="tab" aria-selected={tab === 'open'} className={chip(tab === 'open')}
                  onClick={() => choose('open')}>Open</button>
          <button type="button" role="tab" aria-selected={tab === 'closed'} className={chip(tab === 'closed')}
                  onClick={() => choose('closed')}>Closed</button>
        </div>
        {ownOnly && <p className="mt-1 text-sm text-neutral-600">Showing your tasks on this client</p>}
        {/* the one choice, on both tabs: the Open tab once it holds any task, the Closed tab always once asked, so a
            department with no closed tasks can still be changed back */}
        {(tab === 'open' ? q.data && all.length > 0 : closedAsked) && <div className="mt-2"><DepartmentSelect value={department} onChange={setDepartment} /></div>}
      </div>
      {tab === 'closed' ? (
        closed.error && !closed.data ? (
          <Failure what="Closed tasks" query={closed} />
        ) : !closed.data ? (
          <p className="px-4 pb-3 text-neutral-600">Loading…</p>
        ) : closedTasks.length === 0 ? (
          <p className="px-4 pb-3 text-neutral-600">{department ? `No closed tasks in ${department}.` : 'No closed tasks.'}</p>
        ) : (
          <>
            <ol className="divide-y divide-neutral-100 pb-2">
              {closedTasks.map((t) => <ClosedTaskRow key={t.id} task={t} onOpen={setOpen} />)}
            </ol>
            {closed.hasNextPage && (
              <div className="px-4 pb-3">
                <button type="button" className={plain} disabled={closed.isFetchingNextPage}
                        onClick={() => void closed.fetchNextPage()}>
                  {closed.isFetchingNextPage ? 'Loading…' : 'Show more'}
                </button>
              </div>
            )}
          </>
        )
      ) : q.error && !q.data ? (
        <Failure what="Tasks" query={q} />
      ) : !q.data ? (
        <p className="px-4 pb-3 text-neutral-600">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="px-4 pb-3 text-neutral-600">{department ? `No open tasks in ${department}.` : 'No open tasks.'}</p>
      ) : (
        <ol className="divide-y divide-neutral-100 pb-2">
          {tasks.map((t) => <TaskRow key={t.id} task={t} today={today} onOpen={setOpen} showHolder />)}
        </ol>
      )}
      {open && <TaskCard taskId={open} onClose={() => setOpen(null)} />}
      {sheet === 'new' && <NewTaskSheet client={client} onClose={() => setSheet(null)} />}
    </SectionPanel>
  );
}

// ---------------------------------------------------------------------------- SW

export function SwSection({ clientId, banner, mayRecordVisit }: { clientId: string; banner: ClientBanner; mayRecordVisit: boolean }) {
  const history = useClientTimeline(clientId);
  const whereabouts = useWhereaboutsReports(clientId);
  return (
    <div className="grid12">
      {mayRecordVisit && (
        <div className="panel span-12 px-4 py-3">
          {/* Not for a client who has died or been discharged: nobody visits them. A visit made BEFORE
              can still be written up, as a plain link that says so; the API refuses any dated after. */}
          {isClosed(banner.legal_status) ? (
            <Link to={`/clients/${clientId}/social-work/visit`} className="inline-flex min-h-[44px] items-center text-sm text-neutral-700 underline underline-offset-2">
              {banner.legal_status === 'Discharged' ? 'Record a visit made before the discharge' : 'Record a visit made before their death'}
            </Link>
          ) : (
            <Link to={`/clients/${clientId}/social-work/visit`}
                  className="flex min-h-[52px] w-full items-center justify-center rounded-md bg-brand px-4 font-semibold text-on-brand md:w-auto md:max-w-xs">
              Record a visit
            </Link>
          )}
        </div>
      )}
      <Cell span={6}><WhereaboutsPanel clientId={clientId} placementStart={history.data?.placement_start} /></Cell>
      <Cell span={6}><VisitStandardPanel clientId={clientId} /></Cell>
      <Cell span={12}><CaseTeamPanel clientId={clientId} /></Cell>
      <SectionPanel title="History · placements, stays and visits">
        {history.data ? (
          <>
            <PlacementTimeline entries={history.data.entries} successor={history.data.successor}
                               placementStart={history.data.placement_start} endReasons={whereabouts.data?.end_reasons} />
            {history.data.total > history.data.entries.length && (
              <p className="px-4 pb-3 text-sm text-neutral-500">
                Showing the most recent {history.data.entries.length} of {history.data.total} entries. The earliest are not on this page.
              </p>
            )}
          </>
        ) : history.error ? (
          <Failure what="Placements and stays" query={history} />
        ) : (
          <p className="px-4 py-6 text-neutral-600">Loading history…</p>
        )}
      </SectionPanel>
    </div>
  );
}
